import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import Logo from '@/components/Logo';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    referral_code: params.get('ref') || '',
    turnstile_token: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // --- Cloudflare Turnstile widget -----------------------------------------
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);

  const renderWidget = useCallback(() => {
    if (!window.turnstile || !widgetRef.current || widgetIdRef.current) return;
    widgetIdRef.current = window.turnstile.render(widgetRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token) => setForm((f) => ({ ...f, turnstile_token: token })),
      'expired-callback': () => setForm((f) => ({ ...f, turnstile_token: '' })),
      'error-callback': () => setForm((f) => ({ ...f, turnstile_token: '' })),
    });
  }, []);

  useEffect(() => {
    if (window.turnstile) {
      renderWidget();
      return;
    }
    // api.js script tag loads async — poll briefly until it's ready.
    const id = setInterval(() => {
      if (window.turnstile) {
        clearInterval(id);
        renderWidget();
      }
    }, 150);
    return () => clearInterval(id);
  }, [renderWidget]);

  const resetWidget = () => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
    setForm((f) => ({ ...f, turnstile_token: '' }));
  };
  // --------------------------------------------------------------------------

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.turnstile_token) {
      setError('Please complete the verification check.');
      return;
    }
    setLoading(true); setError('');
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      nav('/dashboard');
    } else {
      setError(res.error);
      resetWidget(); // Turnstile tokens are single-use — must get a fresh one to retry.
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute -top-24 -right-24 w-[560px] h-[560px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(0,255,102,0.18), transparent)' }} />
      </div>
      <div className="relative max-w-md mx-auto px-6 py-14">
        <Link to="/" className="inline-block mb-10" data-testid="register-logo"><Logo size={28} /></Link>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-tech rounded-lg p-8">
          <div className="label-sm mb-2">Join Traders Spring</div>
          <h1 className="font-display text-3xl">Create your account</h1>
          <p className="text-sm text-zinc-500 mt-1">Start your first evaluation in under 2 minutes.</p>

          <form onSubmit={onSubmit} className="space-y-3 mt-6" data-testid="register-form">
            <div>
              <div className="label-xs mb-1">Full name</div>
              <input className="input-tech" type="text" required minLength={2} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Alex Carter" data-testid="register-name" />
            </div>
            <div>
              <div className="label-xs mb-1">Email</div>
              <input className="input-tech" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" data-testid="register-email" />
            </div>
            <div>
              <div className="label-xs mb-1">Password</div>
              <input className="input-tech" type="password" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 characters" data-testid="register-password" />
            </div>
            <div>
              <div className="label-xs mb-1">Referral code (optional)</div>
              <input className="input-tech" type="text" value={form.referral_code} onChange={e => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} placeholder="TSGABC123" data-testid="register-referral" />
            </div>
            <div ref={widgetRef} data-testid="register-turnstile" />
            {error && <div className="text-sm text-[#ff3366]" data-testid="register-error">{error}</div>}
            <button disabled={loading || !form.turnstile_token} type="submit" className="btn-primary w-full justify-center" data-testid="register-submit">
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p className="text-xs text-zinc-500 mt-5 text-center">
            Already have an account? <Link to="/login" className="text-white hover:text-[#00ff66]" data-testid="register-go-login">Sign in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}